import React, { useEffect, useRef, useState } from "react";
import map from "./map";
import { setLeftBottomReserved } from "./animation";
import logLevels from "./schema/logLevels";
import {
  SOURCE_LEVEL,
  SOURCE_SCOPE,
  setIdleFilter,
  setRoleFilter,
  setShowHealth,
  visibility,
} from "./layout/visibility";

// A compact set of toggles in the bottom-right corner: which environments to
// fetch, which roles to draw, and how long a subtree may go quiet before it
// disappears.
//
// Deliberately icon-only and unlabelled. It sits over the map, and the map is the
// point — anything with a caption on it would want more room than the corner has.

const ENVS = ["Live", "Test", "Dev"];

const IDLE_OPTIONS = [
  { label: "10s", ms: 10 * 1000 },
  { label: "1m", ms: 60 * 1000 },
  { label: "5m", ms: 5 * 60 * 1000 },
  { label: "10m", ms: 10 * 60 * 1000 },
  { label: "all", ms: null },
];

// Icons wrap onto a new line past this, so a long tail of roles stays a tidy grid
// rather than one line the panel has to widen for.
const ROLES_PER_ROW = 6;

// The panel's own offset from the bottom edge, plus a little, so a callout never
// sits flush against it.
const PANEL_CLEARANCE = 14;

// The roles present in the map, by icon, with the names that share each one.
// Rebuilt from the live object list rather than from a fixed list, because which
// roles exist is a property of the estate.
function currentRoles() {
  const byEmoji = {};

  for (const key in map.objects) {
    const obj = map.objects[key];
    if (obj.type !== "role" || !obj.data) continue;

    const parts = obj.data.split(":");
    if (parts.length < 2) continue;

    const emoji = parts[1];
    if (!emoji) continue;

    // An icon is not unique to one role, so the tooltip lists everything drawn
    // with it — otherwise switching one off silently takes the others too.
    const names = byEmoji[emoji] || (byEmoji[emoji] = {});
    names[obj.label] = true;
  }

  return Object.keys(byEmoji)
    .sort()
    .map((emoji) => ({
      emoji,
      names: Object.keys(byEmoji[emoji]).sort().join(", "),
    }));
}

function chunk(items, size) {
  const rows = [];
  for (let i = 0; i < items.length; i += size)
    rows.push(items.slice(i, i + size));
  return rows;
}

const FilterPanel = ({ setFilter }) => {
  const [envs, setEnvs] = useState(["Live"]);
  const [knownRoles, setKnownRoles] = useState([]);
  // Which roles are picked out. Empty means no role filter at all, which is the
  // state it starts in.
  const [pickedRoles, setPickedRoles] = useState({});
  const [idleMs, setIdleMs] = useState(visibility.idleMs);
  const [source, setSource] = useState(visibility.source);
  const [showHealth, setHealth] = useState(visibility.showHealth);
  const [logLevel, setLogLevel] = useState(visibility.logLevel);
  const panelRef = useRef(null);

  // Tell the callout layout how much of the bottom-left this is taking, and keep
  // telling it: the panel grows a row every time the estate turns up more role
  // icons than the last one fitted.
  useEffect(() => {
    const element = panelRef.current;
    if (!element) return undefined;

    const report = () =>
      setLeftBottomReserved(element.offsetHeight + PANEL_CLEARANCE);

    report();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(report);
    observer.observe(element);
    return () => {
      observer.disconnect();
      setLeftBottomReserved(0);
    };
  }, []);

  // Roles arrive with the estate and keep arriving, so the set is polled rather
  // than derived once. Cheap: a walk of the object list every few seconds.
  useEffect(() => {
    const tick = () => {
      const found = currentRoles();
      setKnownRoles((prev) =>
        prev.length === found.length &&
        prev.every(
          (r, i) => r.emoji === found[i].emoji && r.names === found[i].names
        )
          ? prev
          : found
      );
    };
    tick();
    const timer = window.setInterval(tick, 3000);
    return () => window.clearInterval(timer);
  }, []);

  const toggleEnv = (env) => {
    const next = envs.includes(env)
      ? envs.filter((e) => e !== env)
      : ENVS.filter((e) => e === env || envs.includes(e));
    setEnvs(next);
    // Everything selected is the same as no filter at all, and asking for none
    // would empty the map — treat both as "show them all".
    setFilter(
      next.length === 0 || next.length === ENVS.length ? null : "envs",
      next.length === 0 || next.length === ENVS.length ? null : next
    );
  };

  // Picking roles narrows to those picked, rather than switching individual ones
  // off — so a single click answers "just show me the workers" instead of needing
  // every other icon turned off one at a time. Picking none means no filter.
  const toggleRole = (emoji) => {
    const next = { ...pickedRoles };
    if (next[emoji]) delete next[emoji];
    else next[emoji] = true;
    setPickedRoles(next);

    setRoleFilter(Object.keys(next).length ? next : null);
  };

  const chooseIdle = (ms) => {
    setIdleMs(ms);
    setIdleFilter(ms, source, logLevel);
  };

  const chooseLevel = (index) => {
    setSource(SOURCE_LEVEL);
    setLogLevel(index);
    setIdleFilter(idleMs, SOURCE_LEVEL, index);
  };

  const chooseScopes = () => {
    setSource(SOURCE_SCOPE);
    setIdleFilter(idleMs, SOURCE_SCOPE, logLevel);
  };

  return (
    <div className="filterPanel" ref={panelRef}>
      <div className="row">
        {ENVS.map((env) => (
          <button
            key={env}
            className={envs.includes(env) ? "chip on" : "chip"}
            onClick={() => toggleEnv(env)}
            title={env}
          >
            {env}
          </button>
        ))}
        <button
          className={showHealth ? "chip health on" : "chip health"}
          onClick={() => {
            setHealth(!showHealth);
            setShowHealth(!showHealth);
          }}
          title={
            showHealth
              ? "health checks included"
              : "health-check scopes hidden, and not counted as activity"
          }
        >
          💓
        </button>
      </div>

      {chunk(knownRoles, ROLES_PER_ROW).map((rowRoles, rowIndex) => (
        <div className="row roles" key={rowIndex}>
          {rowRoles.map(({ emoji, names }) => (
            <button
              key={emoji}
              className={pickedRoles[emoji] ? "chip icon picked" : "chip icon"}
              onClick={() => toggleRole(emoji)}
              title={names || emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      ))}

      <div className="row">
        {IDLE_OPTIONS.map((option) => (
          <button
            key={option.label}
            className={idleMs === option.ms ? "chip on" : "chip"}
            onClick={() => chooseIdle(option.ms)}
            title={
              option.ms == null
                ? "never hide on inactivity"
                : "hide after " + option.label + " with no activity"
            }
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="row levels">
        {logLevels.map((level, index) => (
          <button
            key={level.name}
            className={
              source === SOURCE_LEVEL && logLevel === index
                ? "swatch on"
                : "swatch"
            }
            style={{ background: level.colour }}
            onClick={() => chooseLevel(index)}
            title={level.name + " and above counts as activity"}
          />
        ))}
        <button
          className={
            source === SOURCE_SCOPE ? "swatch scope on" : "swatch scope"
          }
          onClick={chooseScopes}
          title="a scope having run beneath counts as activity"
        >
          ◍
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;
